/**
 * Plan & Billing tab — plan editor, temp unlock, usage rollup, credit.
 */
import { useState, useEffect } from 'react';
import { api } from '../../../api';
import PlanEditor from '../../../components/ops_admin/PlanEditor';
import ReasonField from '../../../components/ops_admin/ReasonField';

export default function OrgPlanTab({ org, opsRole }) {
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canMutate = ['admin', 'owner'].includes(opsRole);

  const [unlockForm, setUnlockForm] = useState({ amount_usd: '', hours: 24, reason: '' });
  const [creditForm, setCreditForm] = useState({ amount_usd: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/ops_admin/orgs/${org.id}/plan`).then(d => setPlan(d.plan)).catch(() => {}),
      api.get(`/ops_admin/orgs/${org.id}/usage`).then(d => setUsage(d)).catch(() => {}),
      api.get(`/ops_admin/orgs/${org.id}/usage/history`).then(d => setHistory(d.history || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [org.id]);

  async function handleUnlock() {
    setSaving(true); setSaveMsg(null);
    try {
      const r = await api.post(`/ops_admin/orgs/${org.id}/plan/unlock`, {
        amount_usd: parseFloat(unlockForm.amount_usd),
        hours: unlockForm.hours,
        reason: unlockForm.reason,
      });
      setPlan(r.plan);
      setSaveMsg('Unlock granted');
      setUnlockForm({ amount_usd: '', hours: 24, reason: '' });
    } catch (e) { setSaveMsg(`Error: ${e.message}`); } finally { setSaving(false); }
  }

  async function handleCredit() {
    setSaving(true); setSaveMsg(null);
    try {
      await api.post(`/ops_admin/orgs/${org.id}/credit`, {
        amount_usd: parseFloat(creditForm.amount_usd),
        reason: creditForm.reason,
      });
      const d = await api.get(`/ops_admin/orgs/${org.id}/usage`);
      setUsage(d);
      setSaveMsg('Credit applied');
      setCreditForm({ amount_usd: '', reason: '' });
    } catch (e) { setSaveMsg(`Error: ${e.message}`); } finally { setSaving(false); }
  }

  if (loading) return <p className="loading-text">Loading plan...</p>;
  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="ops-tab">
      {usage && (
        <div className="usage-summary">
          <h3>Usage — {usage.period}</h3>
          <div className="usage-total">${(usage.total_cost_usd || 0).toFixed(2)} total</div>
          {(Array.isArray(usage.by_service) ? usage.by_service : []).map(row => (
            <div key={row.service} className="usage-row">
              <span>{row.service}</span>
              <span>${(row.total_usd || 0).toFixed(4)}</span>
            </div>
          ))}
        </div>
      )}

      {plan && <PlanEditor orgId={org.id} plan={plan} onSaved={setPlan} />}

      {canMutate && plan?.cap_mode === 'hard' && (
        <div className="ops-section">
          <h3>Temporary Unlock</h3>
          {plan.temp_unlock_expires_at && new Date(plan.temp_unlock_expires_at) > new Date() && (
            <p className="info-text">
              Active unlock: ${plan.temp_unlock_usd} until {new Date(plan.temp_unlock_expires_at).toLocaleString()}
            </p>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Amount (USD)</label>
              <input type="number" className="form-input" value={unlockForm.amount_usd}
                onChange={e => setUnlockForm(f => ({...f, amount_usd: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Duration (hours)</label>
              <input type="number" className="form-input" value={unlockForm.hours}
                onChange={e => setUnlockForm(f => ({...f, hours: parseInt(e.target.value)}))} />
            </div>
          </div>
          <ReasonField value={unlockForm.reason} onChange={r => setUnlockForm(f => ({...f, reason: r}))} />
          <button className="btn btn-primary" onClick={handleUnlock} disabled={saving || !unlockForm.amount_usd || !unlockForm.reason.trim()}>
            Grant Unlock
          </button>
        </div>
      )}

      {canMutate && (
        <div className="ops-section">
          <h3>Apply Credit</h3>
          <div className="form-group">
            <label className="form-label">Amount (USD)</label>
            <input type="number" className="form-input" value={creditForm.amount_usd}
              onChange={e => setCreditForm(f => ({...f, amount_usd: e.target.value}))} />
          </div>
          <ReasonField value={creditForm.reason} onChange={r => setCreditForm(f => ({...f, reason: r}))} />
          <button className="btn btn-primary" onClick={handleCredit}
            disabled={saving || !creditForm.amount_usd || !creditForm.reason.trim()}>
            Apply Credit
          </button>
        </div>
      )}

      {saveMsg && <p className="save-msg">{saveMsg}</p>}

      {history.length > 0 && (
        <div className="ops-section">
          <h3>Billing History</h3>
          <table className="ops-table">
            <thead><tr><th>Period</th><th>Total (USD)</th><th>Events</th></tr></thead>
            <tbody>
              {history.map(h => (
                <tr key={h.period}>
                  <td>{h.period}</td>
                  <td>${(h.total_usd || 0).toFixed(2)}</td>
                  <td>{h.event_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
